"use client";

import { ReactNode, Suspense } from "react";
import { usePathname, useRouter } from "next/navigation";
import { DesignSystemProvider } from "@zapier/design-system";
import Link from "next/link";

import { trpc, useIsNextGenZapsEntitled } from "@zapier/client";
import { IdentityProvider, SessionProvider } from "@zapier/identity";
import { UniversalLayoutWithCookieConsent } from "./UniversalLayoutWithCookieConsent";
import type { EventNameType } from "@zapier/avro-events";
import { NotificationProvider } from "@zapier/utils";
import { useTranslations } from "@zapier/i18n";
import { DefaultViewProvider, logWithNotification } from "@zapier/shared";
import { GlobalSearchInput } from "@zapier/global-search";
import { useAssetSearchErrorMessages } from "./useAssetSearchErrorMessages";
import { useEmitGlobalSearchInteractionEvent } from "@zapier/shared";

import {
  TrackRouting,
  initializeGrafanaFaro,
  DatadogRumUserProvider,
  SprigLoader,
  FullStory,
} from "../observability";
import { DevTools } from "./DevTools";
import { emitEvent } from "@zapier/shared";
import dynamic from "next/dynamic";

const HeldRunsBannerWrapper = dynamic(
  () =>
    import("@zapier/held-runs-embed").then(
      (module) => module.HeldRunsBannerWrapper,
    ),
  { ssr: false },
);

// Exported for its own test: this component is the entire rollout gate.
export function TrackedGlobalSearchInput() {
  const { emit } = useEmitGlobalSearchInteractionEvent();
  const assetSearchErrorMessages = useAssetSearchErrorMessages();

  const { isNextGenZapsEntitled } = useIsNextGenZapsEntitled();

  return (
    <GlobalSearchInput
      emit={emit}
      includeNextGenZaps={isNextGenZapsEntitled}
      assetSearchErrorMessages={assetSearchErrorMessages}
    />
  );
}

interface Props {
  children: ReactNode;
}

initializeGrafanaFaro();

function AppLayout({ children }: Props) {
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations("app-layout");
  //additional assets sidebar is present for all pages except app connections, so we collapse the main nav
  const shouldSidebarBeInitiallyCollapsed = !pathname?.includes("connections");

  return (
    <IdentityProvider>
      <SessionProvider redirectOnUnauthenticated={false}>
        <DatadogRumUserProvider>
          <NotificationProvider notificationLabel={t("notifications-label")}>
            <TrackRouting>
              <UniversalLayoutWithCookieConsent
                shouldSidebarBeInitiallyCollapsed={
                  shouldSidebarBeInitiallyCollapsed
                }
                currentUrl={pathname || ""}
                mainContentId="asset-management-main"
                showSimplifiedFooter={true}
                renderUniversalSearch={() => <TrackedGlobalSearchInput />}
                trackEvent={(eventName: string, options = {}) => {
                  emitEvent(eventName as EventNameType, {
                    ...options,
                  });
                }}
                onTrackingError={(error) => {
                  // TODO: This should be temporary
                  // Should be removed when EE-1603 is addressed
                  // Changes to Universal layout from this MR:
                  // https://gitlab.com/zapier/design-systems/design-system/-/merge_requests/2600
                  logWithNotification(error.message, error.level, {
                    ...error.extra,
                    errorTags: error.tags,
                  });
                }}
              >
                <Suspense>
                  <DefaultViewProvider>
                    <DesignSystemProvider
                      navigate={router.push}
                      LinkComponent={Link}
                    >
                      <DevTools>
                        <HeldRunsBannerWrapper />
                        {children}
                        <FullStory />
                        <SprigLoader />
                      </DevTools>
                    </DesignSystemProvider>
                  </DefaultViewProvider>
                </Suspense>
              </UniversalLayoutWithCookieConsent>
            </TrackRouting>
          </NotificationProvider>
        </DatadogRumUserProvider>
      </SessionProvider>
    </IdentityProvider>
  );
}

export default trpc.withTRPC(AppLayout);
